import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { ProjectCard, Project } from "@/components/projects/ProjectCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Search, Folder, LayoutGrid, List, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      toast.error("Failed to load projects");
    } else {
      setProjects(
        (data || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          description: p.description ?? undefined,
          thumbnail: p.thumbnail ?? undefined,
          createdAt: new Date(p.created_at),
          updatedAt: new Date(p.updated_at),
          materialsCount: p.materials_count ?? 0,
          status: (p.status as "active" | "archived" | "draft") || "draft",
        }))
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const filteredProjects = projects.filter((project) => {
    const matchesSearch = project.name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleProjectClick = (project: Project) => {
    navigate(`/workspace/${project.id}`);
  };

  const handleCreateProject = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from("projects")
      .insert({ user_id: user.id, name: "Untitled Project", status: "draft", materials_count: 0 })
      .select("id")
      .single();

    if (error) {
      toast.error("Failed to create project");
      return;
    }
    toast.success("Project created");
    await fetchProjects();
    navigate(`/workspace/${data.id}`);
  };

  const handleRename = (id: string) => {
    toast.info("Rename dialog would open here");
  };

  const handleDuplicate = async (id: string) => {
    const project = projects.find((p) => p.id === id);
    if (!project) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase.from("projects").insert({
      user_id: user.id,
      name: `${project.name} (Copy)`,
      description: project.description,
      status: "draft",
      materials_count: 0,
    });
    if (error) toast.error("Failed to duplicate");
    else {
      toast.success("Project duplicated");
      fetchProjects();
    }
  };

  const handleArchive = async (id: string) => {
    const { error } = await supabase.from("projects").update({ status: "archived" }).eq("id", id);
    if (error) toast.error("Failed to archive");
    else {
      toast.success("Project archived");
      fetchProjects();
    }
  };

  const handleDelete = (id: string) => {
    setProjectToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (projectToDelete) {
      const { error } = await supabase.from("projects").delete().eq("id", projectToDelete);
      if (error) toast.error("Failed to delete");
      else {
        toast.success("Project deleted");
        fetchProjects();
      }
    }
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 pt-24 pb-8 lg:pt-28 lg:pb-12">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="mb-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
              My Projects
            </h1>
            <p className="text-lg text-muted-foreground">
              Manage your design projects and material combinations
            </p>
          </div>
          <Button onClick={handleCreateProject} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search my projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-card"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] bg-card">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="icon"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Projects Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredProjects.length > 0 ? (
          <div
            className={
              viewMode === "grid"
                ? "grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                : "flex flex-col gap-4"
            }
          >
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onClick={handleProjectClick}
                onRename={handleRename}
                onDuplicate={handleDuplicate}
                onArchive={handleArchive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-4 rounded-full bg-muted p-4">
              <Folder className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mb-2 font-serif text-xl font-medium text-foreground">
              No projects found
            </h3>
            <p className="mb-6 text-muted-foreground">
              {search ? "Try adjusting your search" : "Create your first project to get started"}
            </p>
            {!search && (
              <Button onClick={handleCreateProject} className="gap-2 bg-primary text-primary-foreground">
                <Plus className="h-4 w-4" />
                Create My First Project
              </Button>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-background">
            <DialogHeader>
              <DialogTitle className="font-serif">Delete Project</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this project? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
